import React, { useEffect, useState } from 'react';
import { InstituteAutomationService, InstituteRow, OverviewStats, OverviewListType } from '../instituteAutomationService';
import StatListModal from './StatListModal';

// Live counts from GET /overview/stats. `key` maps to the stats payload; `type` to the drill-down list.
const STATS: { color: string; icon: string; label: string; key: keyof OverviewStats; type: OverviewListType; sub: string }[] = [
    { color: '#f97316', icon: '📅', label: 'Reports Scheduled', key: 'reportsScheduled', type: 'scheduled', sub: 'active schedules' },
    { color: '#0f9d58', icon: '✅', label: 'Sent This Month', key: 'sentThisMonth', type: 'sent', sub: 'reports emailed' },
    { color: '#f59e0b', icon: '⏳', label: 'Pending SM Review', key: 'pendingReview', type: 'pending', sub: 'awaiting approval' },
    { color: '#1b52c4', icon: '🔧', label: 'Active Overrides', key: 'activeOverrides', type: 'overrides', sub: 'client overrides' },
];

const Overview: React.FC<{ onPreview: (inst: InstituteRow) => void }> = ({ onPreview }) => {
    const [query, setQuery] = useState('');
    const [institutes, setInstitutes] = useState<InstituteRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [stats, setStats] = useState<OverviewStats | null>(null);
    const [listType, setListType] = useState<OverviewListType | null>(null);
    const [listTitle, setListTitle] = useState('');
    // The global report frequency applies to ALL institutes; per-institute schedules may add a specific next date.
    const [globalFreqLabel, setGlobalFreqLabel] = useState<string | null>(null);
    const [schedByInstitute, setSchedByInstitute] = useState<Record<string, { next?: string }>>({});

    useEffect(() => {
        let cancelled = false;
        const FREQ_LABEL: Record<number, string> = { 1: 'Monthly', 3: 'Quarterly', 6: 'Semi-Annual', 12: 'Annual' };
        InstituteAutomationService.getGlobalFrequency()
            .then((r) => { if (!cancelled) setGlobalFreqLabel(FREQ_LABEL[r.frequencyMonths] || `${r.frequencyMonths}m`); })
            .catch(() => {});
        InstituteAutomationService.getOverviewList('scheduled')
            .then((items) => {
                if (cancelled) return;
                const map: Record<string, { next?: string }> = {};
                items.forEach((it) => { map[it.instituteId] = { next: it.nextSend }; });
                setSchedByInstitute(map);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Overview stat cards.
    useEffect(() => {
        let cancelled = false;
        InstituteAutomationService.getOverviewStats()
            .then((s) => { if (!cancelled) setStats(s); })
            .catch(() => { if (!cancelled) setStats(null); });
        return () => { cancelled = true; };
    }, []);

    // Debounced institute search (default list = first 20).
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const rows = await InstituteAutomationService.searchInstitutes(query.trim(), 20);
                if (!cancelled) { setInstitutes(rows); setError(false); }
            } catch (_) {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 350 : 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query]);

    return (
        <>
            <div className="row g-3 mb-4">
                {STATS.map((s, i) => {
                    const val = stats ? stats[s.key] : null;
                    const clickable = val != null && val > 0;
                    return (
                        <div className="col-12 col-sm-6 col-md-3" key={i}>
                            <button type="button" disabled={!clickable}
                                onClick={() => { setListType(s.type); setListTitle(s.label); }}
                                className="card border border-light shadow-sm rounded-3 p-3 position-relative overflow-hidden bg-white w-100 text-start border-0"
                                style={{ borderTop: `3px solid ${s.color}`, cursor: clickable ? 'pointer' : 'default' }}>
                                <div className="position-absolute end-0 top-0 p-3 font-24">{s.icon}</div>
                                <div className="text-uppercase text-secondary font-10 font-600 tracking-wide mb-1">{s.label}</div>
                                <div className={`font-28 font-600 font-monospace mb-1 ${val == null ? 'text-secondary' : 'text-dark'}`}>{val == null ? '—' : val}</div>
                                <div className="font-11 text-secondary">{val == null ? 'No data yet' : (clickable ? `${s.sub} · view list →` : s.sub)}</div>
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="row g-4">
                {/* Institute list + search → Preview */}
                <div className="col-12 col-lg-6">
                    <div className="card border border-light shadow-sm rounded-3 bg-white h-100">
                        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                            <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
                                <span className="badge bg-warning bg-opacity-10 text-warning px-2 py-1 font-12">🏫</span>
                                Institutes
                            </div>
                            <span className="text-secondary font-11">Preview a report</span>
                        </div>
                        <div className="px-4 pt-3">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0 text-secondary"><i className="bi bi-search" /></span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm font-13 py-2 border-start-0"
                                    placeholder="Search by institute name…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    style={{ boxShadow: 'none' }}
                                />
                            </div>
                        </div>
                        <div className="card-body p-0 pt-2" style={{ maxHeight: 520, overflowY: 'auto' }}>
                            {loading ? (
                                <div className="text-center text-secondary py-5"><span className="spinner-border spinner-border-sm me-2" />Loading institutes…</div>
                            ) : error ? (
                                <div className="text-center text-secondary py-5 font-12"><i className="bi bi-exclamation-triangle me-1" />Couldn't load institutes.</div>
                            ) : institutes.length === 0 ? (
                                <div className="text-center text-secondary py-5 font-12">No institutes found.</div>
                            ) : institutes.map((inst) => {
                                const sched = schedByInstitute[inst.id];
                                return (
                                    <div className="d-flex align-items-center justify-content-between border-bottom px-4 py-3 gap-2" key={inst.id}>
                                        <div className="min-w-0">
                                            <div className="font-13 font-600 text-dark text-truncate">{inst.name}</div>
                                            <div className="font-11 text-secondary mt-1">
                                                <span className="me-2">🆔 {inst.id}</span>
                                                {inst.type && <span className="me-2">{inst.type}</span>}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            {globalFreqLabel && (
                                                <span className="badge bg-success-subtle text-success font-10 font-600 px-2 py-1 rounded-pill" title={sched?.next ? `Next send ${sched.next}` : 'Global report frequency'}>📅 {globalFreqLabel}</span>
                                            )}
                                            <button className="btn btn-sm btn-outline-secondary rounded-pill font-12 px-2 py-1" onClick={() => onPreview(inst)}>👁 Preview</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-6 d-flex flex-column gap-4">
                    <div className="card border border-light shadow-sm rounded-3 bg-white">
                        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                            <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
                                <span className="badge bg-success bg-opacity-10 text-success px-2 py-1 font-12">✅</span>
                                Recent Sends
                            </div>
                            <span className="text-secondary font-11">Last 30 days</span>
                        </div>
                        <div className="card-body">
                            <div className="text-center text-secondary py-4">
                                <div className="font-28 mb-1">📭</div>
                                <div className="font-12 font-600 text-dark">No reports sent yet</div>
                                <div className="font-11">Sent reports will appear here once the generation pipeline runs.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <StatListModal open={listType !== null} type={listType} title={listTitle} onClose={() => setListType(null)} />
        </>
    );
};

export default Overview;
